/**
 * Pure visibility / activation logic for the header
 * "modules enabled" chips. Kept dependency-free so it can be
 * unit-tested without spinning up jsdom or stubbing axios.
 *
 * The React component lives in `./WorkspaceModuleChips.jsx` and
 * imports from this file.
 */

export const INTERNAL_ROLES = Object.freeze([
  'system_admin', 'meridian_operator', 'meridian_approver',
]);

const INTERNAL_ROLE_SET = new Set(INTERNAL_ROLES);

/**
 * Decide whether the header should render the workspace module chips.
 *
 * Hidden when:
 *   - cross-workspace mode ("View all workspaces"),
 *   - no active workspace is selected,
 *   - the active workspace hasn't been hydrated with any lane info
 *     yet — either the canonical Phase-28 ``lanes_resolved`` block,
 *     or (legacy) ``feature_flags`` / ``enabled_lanes``,
 *   - the current user is not an internal role (v1 is internal-only).
 *
 * Iter S25 Phase 28 (Fix 2) — ``lanes_resolved`` is now the canonical
 * hydration signal. Fall back to legacy shapes only during a rolling
 * upgrade where a workspace record was cached before the backend
 * started emitting ``lanes_resolved``.
 */
export function shouldRenderModuleChips({ role, activeWorkspace,
                                          crossWorkspaceMode }) {
  if (crossWorkspaceMode) return false;
  if (!activeWorkspace) return false;
  const hasLanesResolved = activeWorkspace.lanes_resolved
    && typeof activeWorkspace.lanes_resolved === 'object';
  const hasFeatureFlags = !!activeWorkspace.feature_flags;
  const hasEnabledLanes = Array.isArray(activeWorkspace.enabled_lanes);
  if (!hasLanesResolved && !hasFeatureFlags && !hasEnabledLanes) {
    return false;
  }
  if (!INTERNAL_ROLE_SET.has(role)) return false;
  return true;
}

/**
 * Resolve the active/greyed state of each chip.
 *
 * Iter S25 Phase 28 (Fix 2) — canonical read path.
 * Preferred contract: ``resolveChipFlags(lanesResolved)`` where
 * ``lanesResolved`` is the ``{uk, eu, usa}`` block produced by the
 * backend resolver (services/lanes.py::resolve_lanes). This is the
 * ONLY read path a Phase-28 caller should use.
 *
 * Backwards-compat: if the first arg looks like a legacy
 * ``feature_flags`` object (or is null/undefined) AND a second arg
 * is passed, we fall through to the pre-Phase-28 derivation logic
 * so cached workspace records rendered before the backend emitted
 * ``lanes_resolved`` still light up correctly during a rolling
 * upgrade. No new call site should rely on this — the sidebar,
 * chip row, and home surfaces all resolve lanes on the backend
 * post-Phase-28.
 */
function _isLanesResolvedShape(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    && ('uk' in v || 'eu' in v || 'usa' in v);
}

export function resolveChipFlags(arg1, arg2) {
  // Phase 28 canonical path.
  if (_isLanesResolvedShape(arg1)) {
    return {
      euActive: arg1.eu === true,
      ukActive: arg1.uk === true,
      usaActive: arg1.usa === true,
    };
  }
  // Legacy path — first arg is featureFlags (object or null),
  // second is enabledLanes list. Retained for the transient window
  // where a cached workspace record has no lanes_resolved yet.
  const f = arg1 || {};
  const lanes = Array.isArray(arg2) ? arg2 : [];
  const laneSet = new Set(lanes.map((x) => String(x).toLowerCase()));
  return {
    euActive: f.eu_lane === true,
    ukActive: f.uk_workflows !== false,
    usaActive: laneSet.has('us'),
  };
}
